/**
 * CNAE Form Modal Component
 * Modal for creating/editing company CNAEs
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CompanyCnae,
  CompanyCnaeFormData,
  formatCnaeCode,
  removeCnaeMask,
} from '../../../../types/company';

// Form validation schema
const cnaeSchema = yup.object({
  cnaeCode: yup
    .string()
    .required('Código CNAE é obrigatório')
    .test('cnae-format', 'CNAE deve conter 7 digitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 7;
    }),
  description: yup
    .string()
    .required('Descrição é obrigatória')
    .min(2, 'Descrição deve ter pelo menos 2 caracteres'),
  isPrimary: yup.boolean().default(false),
});

interface CnaeFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyCnaeFormData) => Promise<void>;
  cnae?: CompanyCnae;
  loading?: boolean;
  error?: string | null;
}

const CnaeFormModal: React.FC<CnaeFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  cnae,
  loading = false,
  error,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEditing = !!cnae;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CompanyCnaeFormData>({
    resolver: yupResolver(cnaeSchema) as any,
    defaultValues: {
      cnaeCode: '',
      description: '',
      isPrimary: false,
    },
  });

  // Reset form when modal opens/closes or cnae changes
  useEffect(() => {
    if (open && cnae) {
      reset({
        cnaeCode: formatCnaeCode(cnae.cnaeCode ?? ''),
        description: cnae.description ?? '',
        isPrimary: cnae.isPrimary || false,
      });
    } else if (open && !cnae) {
      reset({
        cnaeCode: '',
        description: '',
        isPrimary: false,
      });
    }
  }, [open, cnae, reset]);

  const handleFormSubmit = async (data: CompanyCnaeFormData) => {
    setSubmitLoading(true);
    try {
      // Remove mask from CNAE code before submitting
      const cleanData = {
        ...data,
        cnaeCode: removeCnaeMask(data.cnaeCode),
      };
      await onSubmit(cleanData);
      onClose();
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar CNAE' : 'Novo CNAE'}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* CNAE Code */}
            <Grid
              size={{
                xs: 12,
                md: 5
              }}>
              <Controller
                name="cnaeCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Código CNAE *"
                    placeholder="XX.XX-X/XX"
                    error={!!errors.cnaeCode}
                    helperText={errors.cnaeCode?.message}
                    onChange={(e) => {
                      const formatted = formatCnaeCode(e.target.value);
                      field.onChange(formatted);
                    }}
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid
              size={{
                xs: 12,
                md: 7
              }}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Descrição *"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>

            {/* Is Primary */}
            <Grid size={12}>
              <Controller
                name="isPrimary"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="CNAE Principal"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitLoading || loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || loading}
          >
            {submitLoading || loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Adicionar'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CnaeFormModal;
